import type { FeedLog } from '../types/production'
import type { InventoryItem } from '../types/inventory'
import { normalizeFeedTypeKey } from './inventoryFeedTypeMatch'

export type DerivedFeedCost = {
  amountCents: number
  currency: string
  matchedItemId: string
}

/**
 * Given a FeedLog and the tenant's inventory, decide whether to auto-derive
 * a paired LotCostEvent (categoryPrefix 'N', direct/variable defaults).
 *
 * Returns null (silent skip) when:
 * - feedType is empty
 * - feedKg is empty, non-numeric, zero, or negative
 * - no matching InventoryItem for (farmId, normalizeFeedTypeKey(feedType))
 *   where the item is category='feed', unit='kg', and unitCostCents > 0
 *
 * Tie-break for multiple matches: usageFeedType exact wins over name exact;
 * within the winning pool, most-recently-updated wins.
 */
export const deriveFeedLogCost = (
  feedLog: Pick<FeedLog, 'farmId' | 'feedType' | 'feedKg'>,
  inventoryItems: readonly InventoryItem[]
): DerivedFeedCost | null => {
  const feedType = feedLog.feedType?.trim() ?? ''
  if (!feedType) return null

  const feedKgRaw = typeof feedLog.feedKg === 'string'
    ? Number(feedLog.feedKg.trim().replaceAll(',', '.'))
    : Number(feedLog.feedKg ?? Number.NaN)
  if (!Number.isFinite(feedKgRaw) || feedKgRaw <= 0) return null

  const key = normalizeFeedTypeKey(feedType)
  if (!key) return null

  const eligible = inventoryItems.filter((item) =>
    item.farmId === feedLog.farmId &&
    item.category === 'feed' &&
    item.unit === 'kg' &&
    typeof item.unitCostCents === 'number' &&
    Number.isFinite(item.unitCostCents) &&
    item.unitCostCents > 0
  )

  if (eligible.length === 0) return null

  const byUsageFeedType = eligible.filter((item) => normalizeFeedTypeKey(item.usageFeedType ?? '') === key)
  const byName = eligible.filter((item) => normalizeFeedTypeKey(item.name ?? '') === key)

  const pool = byUsageFeedType.length > 0 ? byUsageFeedType : byName
  if (pool.length === 0) return null

  const [head, ...rest] = pool
  const matched = rest.reduce(
    (best, curr) => ((curr.updatedAt ?? '') > (best.updatedAt ?? '') ? curr : best),
    head
  )

  const amountCents = Math.round(feedKgRaw * (matched.unitCostCents as number))
  const currency = matched.currency?.trim().toUpperCase() || 'USD'

  return { amountCents, currency, matchedItemId: matched.id }
}
